import { AcquisitionChannel } from "../contracts/AdminAcquisitionSourceResponse";

export interface AdminAcquisitionDay {
  /** ISO calendar date (yyyy-MM-dd), UTC day. */
  day: string;
  pageViews: number;
  metaPageViews: number;
  /** Views where the visitor scrolled past the hero or stayed to read. */
  engaged: number;
  metaEngaged: number;
  ctaTaps: number;
  metaCtaTaps: number;
  arrivals: number;
  metaArrivals: number;
  /** Email-only trial starts: account created and link mailed. */
  trialLinksRequested: number;
  metaTrialLinksRequested: number;
  /** Email-only trial starts whose link or code was used. */
  trialLinksRedeemed: number;
  metaTrialLinksRedeemed: number;
  signUps: number;
}

export interface AdminAcquisitionPage {
  /** The page path without its locale prefix, e.g. `/for/music-teachers`. */
  page: string;
  pageViews: number;
  metaPageViews: number;
  /** Views of this page where the visitor stayed to read rather than bouncing. */
  engaged: number;
  metaEngaged: number;
  ctaTaps: number;
  metaCtaTaps: number;
}

export interface AdminAcquisitionPlacement {
  placement: string;
  ctaTaps: number;
  metaCtaTaps: number;
  arrivals: number;
  metaArrivals: number;
  trialLinksRequested: number;
  metaTrialLinksRequested: number;
}

/** One entry source over the range: where visitors came from and what they did on the site. */
export interface AdminAcquisitionSource {
  channel: AcquisitionChannel;
  /** The UTM source when present, else the referrer host, else `direct`. */
  source: string;
  pageViews: number;
  engaged: number;
  ctaTaps: number;
}

/**
 * The acquisition funnel: marketing-site page views, engaged reads and trial-CTA taps,
 * hand-offs to the sign-up screen, and accounts created — each with the share that came
 * from a Meta ad.
 */
export interface AdminAcquisition {
  fromDay: string;
  toDay: string;
  totalPageViews: number;
  totalMetaPageViews: number;
  totalEngaged: number;
  totalMetaEngaged: number;
  totalCtaTaps: number;
  totalMetaCtaTaps: number;
  totalArrivals: number;
  totalMetaArrivals: number;
  totalTrialLinksRequested: number;
  totalMetaTrialLinksRequested: number;
  totalTrialLinksRedeemed: number;
  totalMetaTrialLinksRedeemed: number;
  totalSignUps: number;
  /** Accounts created since the arrival counter went live — the denominator's honest twin. */
  totalSignUpsSinceArrivalCounter: number;
  days: AdminAcquisitionDay[];
  pages: AdminAcquisitionPage[];
  placements: AdminAcquisitionPlacement[];
  sources: AdminAcquisitionSource[];
  /** Page views with no referrer and no UTM tag. */
  totalDirectPageViews: number;
}

/**
 * Sign-ups per hundred arrivals, or null when there were no arrivals to convert. Pass the
 * accounts created since the arrival counter exists — older accounts had no arrival to count.
 */
export const acquisitionConversionPercent = (arrivals: number, signUps: number): number | null =>
  arrivals === 0 ? null : Math.round((signUps / arrivals) * 1000) / 10;

/**
 * Engaged reads per hundred page views, or null when there were no views. The bounce
 * answer: a low rate means the wrong people are arriving, a high one means they read
 * the page and still didn't act.
 */
export const acquisitionEngagementPercent = (pageViews: number, engaged: number): number | null =>
  pageViews === 0 ? null : Math.round((engaged / pageViews) * 1000) / 10;
